import axios from "axios";
import { getTelegramInitData, getTelegramWebApp, safeLocalStorage, safeSessionStorage } from "@/lib/telegram";
import { normalizeAdminLogsResponse, normalizeAppListResponse, normalizeAppResponse, normalizeBlockedUserListResponse, normalizeCatalogResponse, normalizeCollectionListResponse, normalizeObjectListResponse } from "@/lib/apiResponses";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const TOKEN_KEY = "appway_admin_token";

export const getToken = () => safeLocalStorage.get(TOKEN_KEY);
export const setToken = (t) => safeLocalStorage.set(TOKEN_KEY, t);
export const clearToken = () => safeLocalStorage.remove(TOKEN_KEY);
const authH = () => {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const WB_KEY = "appway_wb";
export const captureWb = () => {
  const wb = new URLSearchParams(window.location.search).get("wb");
  if (wb) safeSessionStorage.set(WB_KEY, wb);
};
const wbContext = () => {
  const wb = safeSessionStorage.get(WB_KEY);
  const wa = getTelegramWebApp();
  return wb ? { wb, init_data: getTelegramInitData(), platform: wa?.platform || "" } : null;
};

export const api = {
  catalog: (config) => axios.get(`${API}/catalog`, config).then((r) => normalizeCatalogResponse(r.data)),
  apps: (params, config) => axios.get(`${API}/apps`, { ...config, params }).then((r) => normalizeAppListResponse(r.data, "/apps")),
  app: (id, config) => axios.get(`${API}/apps/${id}`, config).then((r) => normalizeAppResponse(r.data, `/apps/${id}`)),
  categories: (config) => axios.get(`${API}/categories`, config).then((r) => normalizeObjectListResponse(r.data, "/categories")),
  collections: (config) => axios.get(`${API}/collections`, config).then((r) => normalizeCollectionListResponse(r.data, "/collections")),
  issueAccount: () => {
    const ctx = wbContext();
    const wa = getTelegramWebApp();
    const direct = { init_data: getTelegramInitData(), platform: wa?.platform || "" };
    return (ctx ? axios.post(`${API}/wb/issue`, ctx) : axios.post(`${API}/account/issue`, direct)).then((r) => r.data);
  },
  wbOpen: () => {
    const ctx = wbContext();
    return ctx ? axios.post(`${API}/wb/open`, ctx).then((r) => r.data) : Promise.resolve(null);
  },
  issueStatus: (id) => axios.get(`${API}/account/issue/${id}`).then((r) => r.data),
  issueRevoke: (id) => {
    const wa = getTelegramWebApp();
    return axios.post(`${API}/account/issue/${id}/revoke`, {
      init_data: getTelegramInitData(),
      platform: wa?.platform || "",
    }).then((r) => r.data);
  },

  uploadImage: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return axios
      .post(`${API}/admin/upload`, fd, { headers: { ...authH(), "Content-Type": "multipart/form-data" } })
      .then((r) => r.data);
  },

  login: (email, password) =>
    axios.post(`${API}/auth/login`, { email, password }).then((r) => r.data),
  me: () => axios.get(`${API}/auth/me`, { headers: authH() }).then((r) => r.data),
  stats: () => axios.get(`${API}/admin/stats`, { headers: authH() }).then((r) => r.data),
  adminAccounts: () =>
    axios.get(`${API}/admin/accounts`, { headers: authH() }).then((r) => normalizeObjectListResponse(r.data, "/admin/accounts")),
  adminLogs: (params) =>
    axios.get(`${API}/admin/logs`, { params, headers: authH() }).then((r) => normalizeAdminLogsResponse(r.data)),
  adminSearchUsers: (q) =>
    axios.get(`${API}/admin/users/search`, { params: { q }, headers: authH() }).then((r) => normalizeBlockedUserListResponse(r.data, "/admin/users/search")),
  adminBlockedUsers: () =>
    axios.get(`${API}/admin/blocked-users`, { headers: authH() }).then((r) => normalizeBlockedUserListResponse(r.data, "/admin/blocked-users")),
  adminBlockUser: (body) =>
    axios.post(`${API}/admin/blocked-users`, body, { headers: authH() }).then((r) => r.data),
  adminUnblockUser: (telegramId, reason = "") =>
    axios.post(`${API}/admin/blocked-users/${telegramId}/unblock`, { reason }, { headers: authH() }).then((r) => r.data),
  adminBlockHistory: (telegramId) =>
    axios.get(`${API}/admin/blocked-users/${telegramId}/history`, { headers: authH() }).then((r) => normalizeObjectListResponse(r.data, `/admin/blocked-users/${telegramId}/history`)),
  save: (type, id, body) =>
    (id
      ? axios.put(`${API}/admin/${type}/${id}`, body, { headers: authH() })
      : axios.post(`${API}/admin/${type}`, body, { headers: authH() })
    ).then((r) => r.data),
  remove: (type, id) =>
    axios.delete(`${API}/admin/${type}/${id}`, { headers: authH() }).then((r) => r.data),

  bcDefaultText: () => axios.get(`${API}/admin/broadcast/default-text`, { headers: authH() }).then((r) => r.data),
  bcPreview: (audience) => axios.post(`${API}/admin/broadcast/preview`, { audience }, { headers: authH() }).then((r) => r.data),
  bcTest: (body) => axios.post(`${API}/admin/broadcast/test`, body, { headers: authH() }).then((r) => r.data),
  bcCreate: (body) => axios.post(`${API}/admin/broadcast`, body, { headers: authH() }).then((r) => r.data),
  bcList: () => axios.get(`${API}/admin/broadcasts`, { headers: authH() }).then((r) => normalizeObjectListResponse(r.data, "/admin/broadcasts")),
  bcGet: (id) => axios.get(`${API}/admin/broadcast/${id}`, { headers: authH() }).then((r) => r.data),
  bcConfirm: (id) => axios.post(`${API}/admin/broadcast/${id}/confirm`, {}, { headers: authH() }).then((r) => r.data),
  bcCancel: (id) => axios.post(`${API}/admin/broadcast/${id}/cancel`, {}, { headers: authH() }).then((r) => r.data),
};
